import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router"
import { NotificationSettings } from "../../../App/enums"
import { selectTranslations } from "../../../App/i18n/i18nSlice"
import { OrganizationEmailType, OrganizationPushType, OrganizationWebType } from "../../../Types/Notification"
import EmailNotification from "../../Shared/Components/EmailNotifications"
import { LoadingAnimation, MessageActiveIcon, NotificationsIcon, WebNotificationIcon } from "../../Shared/Components/SvgIcons"
import WebNotifications from "../../Shared/Components/WebNotification"
import Button from "../../Shared/Primitive/Button"
import {
    organizationEmailNotificationsSettingsGetRequest,
    organizationPushNotificationsSettingsGetRequest,
    organizationPushNotificationsSettingsPatchRequest,
    organizationEmailNotificationsSettingsPatchRequest,
    organizationWebNotificationsSettingsGetRequest,
    organizationWebNotificationsSettingsPatchRequest,
} from "./OrganizationNotificationsServiceRequests"
import { getOrganizationNotificationsSettingsState } from "./OrganizationSettingsSlice"
import useWindowSize from "../../Shared/Hooks/useWindowSize"

function OrganizationNotificationSettings() {
    const translation = useSelector(selectTranslations)
    const [webNotification, setWebNotification] = useState<OrganizationWebType>()
    const [emailNotification, setEmailNotification] = useState<OrganizationEmailType>()
    const { id } = useParams()
    const screenWidth = useWindowSize()
    const breakpoint = 768
    const dispatch = useDispatch()

    const [isLoading, setIsLoading] = useState(true)
    const [pushNotification, setPushNotification] = useState<OrganizationPushType>()
    useEffect(() => {
        organizationEmailNotificationsSettingsGetRequest(id!)
            .then(response => {
                if (response.status === 200) {
                    setEmailNotification(response.data)
                }
            })
            .catch(error => {
                console.log(error)
            })

        organizationWebNotificationsSettingsGetRequest(id!)
            .then(response => {
                if (response.status === 200) {
                    setWebNotification({
                        comment: { value_none: !response.data.comment, value_immediate: response.data.comment },
                        commented_on_same_post: { value_none: !response.data.commented_on_same_post, value_immediate: response.data.commented_on_same_post },
                        tags: { value_none: !response.data.tags, value_immediate: response.data.tags },
                        org_room_invitation: { value_none: !response.data.org_room_invitation, value_immediate: response.data.org_room_invitation },
                        room_invitation_reply: { value_none: !response.data.room_invitation_reply, value_immediate: response.data.room_invitation_reply },
                        room_join_request: { value_none: !response.data.room_join_request, value_immediate: response.data.room_join_request },
                        room_join_request_reply: { value_none: !response.data.room_join_request_reply, value_immediate: response.data.room_join_request_reply },
                        new_member: { value_none: !response.data.new_member, value_immediate: response.data.new_member },
                        event_comment: { value_none: !response.data.event_comment, value_immediate: response.data.event_comment },
                        new_post_in_room: { value_none: !response.data.new_post_in_room, value_immediate: response.data.new_post_in_room },
                    })
                }
            })
            .catch(error => {
                console.log(error)
            })

        organizationPushNotificationsSettingsGetRequest(id!)
            .then(response => {
                if (response.status === 200) {
                    setPushNotification(response.data)
                }
            })
            .catch(error => {
                console.log(error)
            })
    }, [id])

    useEffect(() => {
        if (webNotification !== undefined && emailNotification !== undefined && pushNotification !== undefined) {
            setIsLoading(false)
        }
    }, [emailNotification, webNotification, pushNotification])

    const handleOnSubmit = (event: { preventDefault: () => void }) => {
        event.preventDefault()
        const webNotifications = {
            comment: webNotification?.comment.value_immediate!,
            commented_on_same_post: webNotification?.commented_on_same_post.value_immediate!,
            tags: webNotification?.tags.value_immediate!,
            org_room_invitation: webNotification?.org_room_invitation.value_immediate!,
            room_invitation_reply: webNotification?.room_invitation_reply.value_immediate!,
            room_join_request: webNotification?.room_join_request.value_immediate!,
            room_join_request_reply: webNotification?.room_join_request_reply.value_immediate!,
            new_member: webNotification?.new_member.value_immediate!,
            event_comment: webNotification?.event_comment.value_immediate!,
            new_post_in_room: webNotification?.new_post_in_room.value_immediate!,
        }

        const emailNotifications = {
            comment: emailNotification?.comment.value_none ? 0 : emailNotification?.comment.value_daily ? 1 : 2,
            commented_on_same_post: emailNotification?.commented_on_same_post.value_none ? 0 : emailNotification?.commented_on_same_post.value_daily ? 1 : 2,
            tags: emailNotification?.tags.value_none ? 0 : emailNotification?.tags.value_daily ? 1 : 2,
            room_invitation: emailNotification?.room_invitation.value_none ? 0 : emailNotification?.room_invitation.value_daily ? 1 : 2,
            room_invitation_reply: emailNotification?.room_invitation_reply.value_none ? 0 : emailNotification?.room_invitation_reply.value_daily ? 1 : 2,
            room_join_request: emailNotification?.room_join_request.value_none ? 0 : emailNotification?.room_join_request.value_daily ? 1 : 2,
            room_join_request_reply: emailNotification?.room_join_request_reply.value_none ? 0 : emailNotification?.room_join_request_reply.value_daily ? 1 : 2,
            new_member: emailNotification?.new_member.value_none ? 0 : emailNotification?.new_member.value_daily ? 1 : 2,
            event_comment: emailNotification?.event_comment.value_none ? 0 : emailNotification?.event_comment.value_daily ? 1 : 2,
            new_post_in_room: emailNotification?.new_post_in_room.value_none ? 0 : emailNotification?.new_post_in_room.value_daily ? 1 : 2,
            upcoming_events: emailNotification?.upcoming_events.value_none ? 0 : emailNotification?.upcoming_events.value_daily ? 1 : 2,
        }

        const pushNotifications = {
            enabled: pushNotification?.enabled.value_immediate!,
            comment: pushNotification?.comment.value_immediate!,
            commented_on_same_post: pushNotification?.commented_on_same_post.value_immediate!,
            tags: pushNotification?.tags.value_immediate!,
            org_room_invitation: pushNotification?.org_room_invitation.value_immediate!,
            room_invitation_reply: pushNotification?.room_invitation_reply.value_immediate!,
            room_join_request: pushNotification?.room_join_request.value_immediate!,
            room_join_request_reply: pushNotification?.room_join_request_reply.value_immediate!,
            new_member: pushNotification?.new_member.value_immediate!,
            event_comment: pushNotification?.event_comment.value_immediate!,
            new_post_in_room: pushNotification?.new_post_in_room.value_immediate!,
        }

        organizationWebNotificationsSettingsPatchRequest(webNotifications, id!)
        organizationEmailNotificationsSettingsPatchRequest(emailNotifications, id!)
        organizationPushNotificationsSettingsPatchRequest(pushNotifications, id!)
        dispatch(getOrganizationNotificationsSettingsState(false))
    }

    return (
        <form className="notification-settings" onSubmit={handleOnSubmit}>
            {isLoading ? (
                <div className="settings-right-panel__settings-notifications-loading-container">
                    <LoadingAnimation />
                </div>
            ) : (
                <div className="notification-settings__form">
                    <div className="notification-settings__first-row">
                        <div className="notification-settings__first-row-start">
                            <MessageActiveIcon />
                            <p className="notification-settings__first-row-email">{translation.byEmail}</p>
                        </div>
                        <div className="notification-settings__first-row-end">
                            <p className="notification-settings__first-row-none">{translation.none}</p>
                            <p className="notification-settings__first-row-daily">{translation.daily}</p>
                            <p className="notification-settings__first-row-weekly">{translation.weekly}</p>
                        </div>
                    </div>
                    <div className="notification-settings__container">
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.commentUser}</p>
                            <EmailNotification
                                emailState={emailNotification?.comment!}
                                setEmailState={setEmailNotification}
                                type={NotificationSettings.comment}
                                data={emailNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.commentOnSamePost}</p>
                            <EmailNotification
                                emailState={emailNotification?.commented_on_same_post!}
                                setEmailState={setEmailNotification}
                                type={NotificationSettings.commented_on_same_post}
                                data={emailNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.roomInvitationUser}</p>
                            <EmailNotification
                                emailState={emailNotification?.room_invitation!}
                                setEmailState={setEmailNotification}
                                type={NotificationSettings.room_invitation}
                                data={emailNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.roomInvitationReplyUser}</p>
                            <EmailNotification
                                emailState={emailNotification?.room_invitation_reply!}
                                setEmailState={setEmailNotification}
                                type={NotificationSettings.room_invitation_reply}
                                data={emailNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.eventCommentOrganization}</p>
                            <EmailNotification
                                emailState={emailNotification?.event_comment!}
                                setEmailState={setEmailNotification}
                                type={NotificationSettings.event_comment}
                                data={emailNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.newMembersOrganization}</p>
                            <EmailNotification
                                emailState={emailNotification?.new_member!}
                                setEmailState={setEmailNotification}
                                type={NotificationSettings.new_member}
                                data={emailNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.newPostInRoomOrganization}</p>
                            <EmailNotification
                                emailState={emailNotification?.new_post_in_room!}
                                setEmailState={setEmailNotification}
                                type={NotificationSettings.new_post_in_room}
                                data={emailNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.roomJoinRequestOrganization}</p>
                            <EmailNotification
                                emailState={emailNotification?.room_join_request!}
                                setEmailState={setEmailNotification}
                                type={NotificationSettings.room_join_request}
                                data={emailNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.roomJoinRequestReplyUser}</p>
                            <EmailNotification
                                emailState={emailNotification?.room_join_request_reply!}
                                setEmailState={setEmailNotification}
                                type={NotificationSettings.room_join_request_reply}
                                data={emailNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.tagsUser}</p>
                            <EmailNotification
                                emailState={emailNotification?.tags!}
                                setEmailState={setEmailNotification}
                                type={NotificationSettings.tags}
                                data={emailNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.upcomingEventsShort}</p>
                            <EmailNotification
                                emailState={emailNotification?.upcoming_events!}
                                setEmailState={setEmailNotification}
                                type={NotificationSettings.upcoming_events}
                                data={emailNotification}
                            />
                        </div>
                    </div>

                    <div className="notification-settings__line"></div>

                    <div className="notification-settings__first-row">
                        <div className="notification-settings__first-row-start">
                            <WebNotificationIcon />
                            <p className="notification-settings__first-row-email">{translation.byWeb}</p>
                        </div>
                        <div className="notification-settings__first-row-end">
                            <p className="notification-settings__first-row-none">{translation.none}</p>
                            <p className="notification-settings__first-row-immediate">{translation.immediate}</p>
                        </div>
                    </div>
                    <div className="notification-settings__container">
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.commentUser}</p>
                            <WebNotifications
                                webState={webNotification?.comment!}
                                setWebState={setWebNotification}
                                type={NotificationSettings.comment}
                                data={webNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.commentOnSamePost}</p>
                            <WebNotifications
                                webState={webNotification?.commented_on_same_post!}
                                setWebState={setWebNotification}
                                type={NotificationSettings.commented_on_same_post}
                                data={webNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.roomInvitationUser}</p>
                            <WebNotifications
                                webState={webNotification?.org_room_invitation!}
                                setWebState={setWebNotification}
                                type={NotificationSettings.org_room_invitation}
                                data={webNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.roomInvitationReplyUser}</p>
                            <WebNotifications
                                webState={webNotification?.room_invitation_reply!}
                                setWebState={setWebNotification}
                                type={NotificationSettings.room_invitation_reply}
                                data={webNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.eventCommentOrganization}</p>
                            <WebNotifications
                                webState={webNotification?.event_comment!}
                                setWebState={setWebNotification}
                                type={NotificationSettings.event_comment}
                                data={webNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.newMembersOrganization}</p>
                            <WebNotifications
                                webState={webNotification?.new_member!}
                                setWebState={setWebNotification}
                                type={NotificationSettings.new_member}
                                data={webNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.newPostInRoomOrganization}</p>
                            <WebNotifications
                                webState={webNotification?.new_post_in_room!}
                                setWebState={setWebNotification}
                                type={NotificationSettings.new_post_in_room}
                                data={webNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.roomJoinRequestOrganization}</p>
                            <WebNotifications
                                webState={webNotification?.room_join_request!}
                                setWebState={setWebNotification}
                                type={NotificationSettings.room_join_request}
                                data={webNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.roomJoinRequestReplyUser}</p>
                            <WebNotifications
                                webState={webNotification?.room_join_request_reply!}
                                setWebState={setWebNotification}
                                type={NotificationSettings.room_join_request_reply}
                                data={webNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.tagsUser}</p>
                            <WebNotifications
                                webState={webNotification?.tags!}
                                setWebState={setWebNotification}
                                type={NotificationSettings.tags}
                                data={webNotification}
                            />
                        </div>
                    </div>

                    <div className="notification-settings__line"></div>

                    <div className="notification-settings__first-row">
                        <div className="notification-settings__first-row-start">
                            <NotificationsIcon />
                            <p className="notification-settings__first-row-email">{translation.byPush}</p>
                        </div>
                        <div className="notification-settings__first-row-end">
                            <p className="notification-settings__first-row-none">{translation.none}</p>
                            <p className="notification-settings__first-row-immediate">{translation.immediate}</p>
                        </div>
                    </div>
                    <div className="notification-settings__container">
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.pushEnabled}</p>
                            <WebNotifications
                                webState={pushNotification?.enabled!}
                                setWebState={setPushNotification}
                                type={NotificationSettings.enabled}
                                data={pushNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.commentUser}</p>
                            <WebNotifications
                                webState={pushNotification?.comment!}
                                setWebState={setPushNotification}
                                type={NotificationSettings.comment}
                                data={pushNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.commentOnSamePost}</p>
                            <WebNotifications
                                webState={pushNotification?.commented_on_same_post!}
                                setWebState={setPushNotification}
                                type={NotificationSettings.commented_on_same_post}
                                data={pushNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.roomInvitationUser}</p>
                            <WebNotifications
                                webState={pushNotification?.org_room_invitation!}
                                setWebState={setPushNotification}
                                type={NotificationSettings.org_room_invitation}
                                data={pushNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.roomInvitationReplyUser}</p>
                            <WebNotifications
                                webState={pushNotification?.room_invitation_reply!}
                                setWebState={setPushNotification}
                                type={NotificationSettings.room_invitation_reply}
                                data={pushNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.eventCommentOrganization}</p>
                            <WebNotifications
                                webState={pushNotification?.event_comment!}
                                setWebState={setPushNotification}
                                type={NotificationSettings.event_comment}
                                data={pushNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.newMembersOrganization}</p>
                            <WebNotifications
                                webState={pushNotification?.new_member!}
                                setWebState={setPushNotification}
                                type={NotificationSettings.new_member}
                                data={pushNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.newPostInRoomOrganization}</p>
                            <WebNotifications
                                webState={pushNotification?.new_post_in_room!}
                                setWebState={setPushNotification}
                                type={NotificationSettings.new_post_in_room}
                                data={pushNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.roomJoinRequestOrganization}</p>
                            <WebNotifications
                                webState={pushNotification?.room_join_request!}
                                setWebState={setPushNotification}
                                type={NotificationSettings.room_join_request}
                                data={pushNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.roomJoinRequestReplyUser}</p>
                            <WebNotifications
                                webState={pushNotification?.room_join_request_reply!}
                                setWebState={setPushNotification}
                                type={NotificationSettings.room_join_request_reply}
                                data={pushNotification}
                            />
                        </div>
                        <div className="notification-settings__title-container">
                            <p className="notification-settings__email-comment-title">{translation.tagsUser}</p>
                            <WebNotifications
                                webState={pushNotification?.tags!}
                                setWebState={setPushNotification}
                                type={NotificationSettings.tags}
                                data={pushNotification}
                            />
                        </div>
                    </div>

                    <div className="notification-settings__button-container">
                        <div className="notification-settings__button">
                            <Button
                                type="submit"
                                className={`button ${
                                    screenWidth > breakpoint && "button--border-radius-05"
                                } button--color-gold button--hover-brighter button--cursor-pointer button--text-color-white button--font-size-small button--font-weight-400 `}
                            >
                                {translation.save}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </form>
    )
}

export default OrganizationNotificationSettings
